/**
 * Wraps a legal document body in a full HTML page styled to match the Guardian
 * app design tokens (Open Sans, #252525 text, #003795 links). Rendered inside
 * the {@link AuthLegalPopup} WebView, so the page is transparent and relies on
 * the surrounding modal card for its background/padding.
 *
 * Metro cannot import raw `.html` files as strings, so the two legal documents
 * live as string modules (`privacy-policy.ts`, `terms-of-service.ts`) and are
 * composed through this helper — each export is a complete, standalone HTML
 * document you can drop into any WebView.
 */
export function buildLegalDocumentHtml(bodyHtml: string): string {
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
    <style>
      :root { color-scheme: light; }
      * { box-sizing: border-box; }
      html, body {
        margin: 0;
        padding: 0;
        background: transparent;
        -webkit-text-size-adjust: 100%;
      }
      body {
        font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        color: #252525;
        font-size: 14px;
        line-height: 1.4;
        /* Leave room on the right for the native scrollbar; the modal card owns the outer padding. */
        padding: 2px 4px 24px 0;
      }
      section { margin-bottom: 16px; }
      section:last-child { margin-bottom: 0; }
      h2 {
        margin: 0 0 8px;
        font-size: 16px;
        font-weight: 700;
        line-height: 1.6;
      }
      p { margin: 0 0 8px; }
      p:last-child { margin-bottom: 0; }
      ul {
        margin: 0;
        padding-left: 20px;
      }
      li { margin-bottom: 4px; }
      li:last-child { margin-bottom: 0; }
      a {
        color: #003795;
        text-decoration: underline;
        text-underline-position: from-font;
      }
      strong { font-weight: 700; }
      .muted { color: #64748b; }
    </style>
  </head>
  <body>
${bodyHtml}
  </body>
</html>`;
}
